import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { Briefcase, Link as LinkIcon, MessagesSquare } from "lucide-react";
import React from "react";
import { useGetCustomerEmailsDataQuery } from "../../../store/services/b2bMailService";
import { ProcessedEmail } from "../types";

interface WorksListProps {
  customerNo: string;
  email: ProcessedEmail;
  onConfirmMatch: (workId: number, eventDataId?: number) => void;
}

export const WorksList: React.FC<WorksListProps> = ({
  customerNo,
  email,
  onConfirmMatch,
}) => {
  const { data: emailData, isFetching } = useGetCustomerEmailsDataQuery({
    CustomerNo: customerNo,
  });

  if (isFetching) {
    return (
      <Box sx={{ my: 2 }}>
        {[1, 2].map((i) => (
          <Box key={i} sx={{ display: "flex", gap: 2, mb: 2 }}>
            <Skeleton variant="circular" width={24} height={24} />
            <Skeleton variant="rectangular" width="100%" height={40} />
          </Box>
        ))}
      </Box>
    );
  }

  const openWorks = emailData?.OpenWorks || [];
  const conversations = emailData?.Conversations || [];

  // Check if this exact email is already connected (exact ID match)
  const exactMatch = conversations.find((c) => c.Id === email.id);
  if (exactMatch) {
    const work = openWorks.find((w) => w.Id === exactMatch.WorkId);
    return (
      <Alert severity="info" sx={{ mt: 1 }}>
        Detta mail är redan kopplat till ärende: {work?.Name}
      </Alert>
    );
  }

  const latestConversations = conversations
    .filter((c) => c.ConversationId === email.conversationId) // Filter only matching conversations
    .reduce((acc, current) => {
      const existingConversation = acc.find(
        (c) => c.ConversationId === current.ConversationId
      );
      if (!existingConversation) {
        acc.push(current);
      }
      return acc;
    }, [] as typeof conversations);
  console.log(latestConversations);
  return (
    <Stack spacing={2}>
      {/* Visa relaterade konversationer om de finns */}
      {latestConversations.length > 0 && (
        <Box>
          <Typography
            variant="subtitle2"
            color="primary"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 1,
            }}
          >
            <MessagesSquare size={16} />
            Tidigare konversation(er)
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {latestConversations.map((conv, index) => {
              const work = openWorks.find((w) => w.Id === conv.WorkId);
              return (
                <Stack
                  key={index}
                  direction="row"
                  gap={1}
                  alignItems="center"
                  justifyContent={"space-between"}
                  sx={{
                    bgcolor: "background.paper",
                    borderRadius: 1,
                    p: 1,
                    "&:hover": {
                      bgcolor: "action.hover",
                    },
                    width: "100%",
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {work ? work.Name : `Ärende ${conv.WorkId}`}
                  </Typography>
                  <Chip
                    icon={<LinkIcon size={14} />}
                    label={"Koppla mail"}
                    color="primary"
                    variant="outlined"
                    size="small"
                    onClick={() =>
                      onConfirmMatch(conv.WorkId, conv.EventDataId)
                    }
                  />
                </Stack>
              );
            })}
          </Stack>
        </Box>
      )}

      {/* Divider mellan sektioner om båda finns */}
      {latestConversations.length > 0 && openWorks.length > 0 && <Divider />}

      {/* Lista öppna ärenden */}
      {openWorks.length > 0 && latestConversations.length === 0 && (
        <Stack spacing={1}>
          <Typography
            variant="subtitle2"
            color="primary"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Briefcase size={16} />
            Öppna ärenden på kund
          </Typography>

          <List dense sx={{ bgcolor: "background.paper" }}>
            {openWorks.map((work) => (
              <ListItem
                key={work.Id}
                secondaryAction={
                  <Chip
                    icon={<LinkIcon size={14} />}
                    label={"Koppla mail"}
                    color="primary"
                    variant="outlined"
                    size="small"
                    onClick={() => onConfirmMatch(work.Id)}
                  />
                }
                sx={{
                  borderRadius: 1,
                  "&:hover": {
                    bgcolor: "action.hover",
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Briefcase size={16} />
                </ListItemIcon>
                <ListItemText
                  primary={work.Name}
                  secondary={
                    <Stack>
                      <Typography
                        variant="caption"
                        component="span"
                        color="text.secondary"
                      >
                        • Ansvarig: {work.AssignedTo?.Name || "Ingen"}
                      </Typography>
                      <Typography
                        variant="caption"
                        component="span"
                        color="text.secondary"
                      >
                        • {new Date(work.Created).toLocaleDateString("sv-SE")}
                      </Typography>
                    </Stack>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Stack>
      )}

      {/* Visa meddelande om inga ärenden finns */}
      {!latestConversations.length && !openWorks.length && (
        <>
          <Typography variant="body2" color="text.secondary">
            Inga öppna ärenden eller tidigare konversationer
          </Typography>
          <Button>Skapa ärende</Button>
        </>
      )}
    </Stack>
  );
};
